/**
 * Standort auf dem Gerät (27.10.2026): Orte des Haushalts und Check-ins.
 *
 * Gewöhnliche Sync-Zeilen — lokal Klartext, beim Server immer verschlüsselt
 * (`ALWAYS_SEALED_TABLES`). Ohne Haushaltsschlüssel bleiben sie in der
 * Outbox (`prepareForPush`), die Ansicht sagt es (`heldLocationCount`).
 */
import { LOCATION_SEALED_TABLES, keptCheckins, newId } from '@kraftwerk/shared';
import type { CheckinKind, CheckinNotice, GeoPoint } from '@kraftwerk/shared';
import { apiFetch } from '../api.js';
import { currentOwnerId } from '../auth.js';
import { db } from './local.js';
import type { KraftwerkDatabase, LocalCheckin, LocalPlace } from './local.js';
import { stamp, writeRow } from './write.js';

export interface Fix extends GeoPoint {
  /** Genauigkeit in Metern, wie das Gerät sie meldet. */
  accuracyM: number | null;
  /** Wann der Ort bestimmt wurde (ISO). */
  at: string;
}

// --- Orte ----------------------------------------------------------------------

/** Orte mit Inhalt — ein noch verschlossener (ohne Schlüssel) fehlt. */
export async function listPlaces(
  householdId: string | null,
  database: KraftwerkDatabase = db,
): Promise<LocalPlace[]> {
  if (!householdId) return [];
  return (await database.places.where('householdId').equals(householdId).toArray())
    .filter((place) => !place.deletedAt && place.name)
    .sort((a, b) => a.name.localeCompare(b.name, 'de'));
}

export async function addPlace(
  input: { householdId: string; name: string; point: GeoPoint; radiusM: number },
  database: KraftwerkDatabase = db,
): Promise<LocalPlace> {
  const me = currentOwnerId();
  const row: LocalPlace = {
    id: newId(),
    ...(me ? { userId: me } : {}),
    householdId: input.householdId,
    name: input.name.trim(),
    latitude: input.point.latitude,
    longitude: input.point.longitude,
    radiusM: Math.round(input.radiusM),
    updatedAt: stamp(),
    deletedAt: null,
  };
  return writeRow('places', row, 'upsert', database);
}

export async function deletePlace(id: string, database: KraftwerkDatabase = db): Promise<void> {
  const place = await database.places.get(id);
  if (!place || place.deletedAt) return;
  const now = stamp();
  await writeRow('places', { ...place, deletedAt: now, updatedAt: now }, 'delete', database);
}

// --- Check-ins -------------------------------------------------------------------

/** Was die Aufbewahrung behält, neueste zuerst (`keptCheckins`). */
export async function listCheckins(
  householdId: string | null,
  now: Date,
  database: KraftwerkDatabase = db,
): Promise<LocalCheckin[]> {
  if (!householdId) return [];
  const rows = (
    await database.location_checkins.where('householdId').equals(householdId).toArray()
  ).filter((row) => row.kind && row.latitude !== null && row.longitude !== null);
  return keptCheckins(rows, now);
}

/** Wie der Haushalt mich nennt — für die Mitteilung. */
async function myName(householdId: string, database: KraftwerkDatabase): Promise<string> {
  const me = currentOwnerId();
  if (!me) return '';
  const member = (await database.household_members.where('userId').equals(me).toArray()).find(
    (row) => row.householdId === householdId && !row.deletedAt,
  );
  return member?.displayName ?? '';
}

export async function checkIn(
  input: {
    householdId: string;
    kind: CheckinKind;
    fix: Fix;
    place: { id: string; name: string } | null;
    /** Nur bei `live`: bis wann geteilt wird. */
    liveUntil?: string | null;
  },
  database: KraftwerkDatabase = db,
): Promise<LocalCheckin> {
  const me = currentOwnerId();
  const notice: CheckinNotice = {
    kind: input.kind,
    name: await myName(input.householdId, database),
    place: input.place?.name ?? null,
    until: input.liveUntil ?? null,
  };
  const row: LocalCheckin = {
    id: newId(),
    ...(me ? { userId: me } : {}),
    householdId: input.householdId,
    kind: input.kind,
    latitude: input.fix.latitude,
    longitude: input.fix.longitude,
    accuracyM: input.fix.accuracyM === null ? null : Math.round(input.fix.accuracyM),
    placeId: input.place?.id ?? null,
    placeName: input.place?.name ?? null,
    recordedAt: input.fix.at,
    liveUntil: input.liveUntil ?? null,
    notice,
    updatedAt: stamp(),
    deletedAt: null,
  };
  return writeRow('location_checkins', row, 'upsert', database);
}

/**
 * Teilen, solange die App offen ist: neuer Punkt an derselben Zeile — ohne
 * Mitteilung (gemeldet wird nur der Beginn).
 */
export async function updateLive(
  id: string,
  fix: Fix,
  place: { id: string; name: string } | null,
  database: KraftwerkDatabase = db,
): Promise<void> {
  const row = await database.location_checkins.get(id);
  if (!row || row.deletedAt) return;
  await writeRow(
    'location_checkins',
    {
      ...row,
      latitude: fix.latitude,
      longitude: fix.longitude,
      accuracyM: fix.accuracyM === null ? null : Math.round(fix.accuracyM),
      placeId: place?.id ?? null,
      placeName: place?.name ?? null,
      recordedAt: fix.at,
      notice: null,
      updatedAt: stamp(),
    },
    'upsert',
    database,
  );
}

/** Teilen beenden: Der letzte Punkt bleibt stehen, „bis“ wird jetzt. */
export async function stopLive(id: string, database: KraftwerkDatabase = db): Promise<void> {
  const row = await database.location_checkins.get(id);
  if (!row || row.deletedAt || row.kind !== 'live') return;
  const now = stamp();
  if (row.liveUntil && row.liveUntil <= now) return;
  await writeRow(
    'location_checkins',
    { ...row, liveUntil: now, notice: null, updatedAt: now },
    'upsert',
    database,
  );
}

/** Eigenen Check-in zurücknehmen — weich, damit es jedes Gerät erfährt. */
export async function deleteCheckin(id: string, database: KraftwerkDatabase = db): Promise<void> {
  const row = await database.location_checkins.get(id);
  if (!row || row.deletedAt) return;
  const now = stamp();
  await writeRow(
    'location_checkins',
    { ...row, notice: null, deletedAt: now, updatedAt: now },
    'delete',
    database,
  );
}

/**
 * Wie viele Standort-Änderungen auf den Haushaltsschlüssel warten. Sie gehen
 * nie als Klartext hinaus; ohne Schlüssel bleiben sie in der Outbox.
 */
export async function heldLocationCount(
  householdId: string | null,
  database: KraftwerkDatabase = db,
): Promise<number> {
  // Flach gelesen (Live-Abfrage): nur nachsehen, ob der Schlüssel da ist.
  if (!householdId || (await database.meta.get(`hk:${householdId}`))) return 0;
  return database.outbox
    .where('table')
    .anyOf([...LOCATION_SEALED_TABLES])
    .count();
}

/** Standort eines Kindes an/aus — nur Erwachsene, schreibt der Server (`/households/children/:id/location`). */
export async function setLocationSharing(childId: string, enabled: boolean): Promise<void> {
  await apiFetch(`/households/children/${encodeURIComponent(childId)}/location`, {
    method: 'POST',
    body: JSON.stringify({ enabled }),
  });
}
